<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { usePage } from '@inertiajs/vue3';
import NetworkNode from '@/Components/User/NetworkNode.vue';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useTranslation} from "@/composables/useTranslation.js";

const { t } = useTranslation();

const props = defineProps({
    network: {
        type: Array,
        default: () => []
    },
    stats: {
        type: Object,
        default: () => ({
            total_network: 0,
            active_members: 0,
            total_earnings: 0,
            network_depth: 0
        })
    },
    referralBonuses: {
        type: Array,
        default: () => []
    }
});

const page = usePage();

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const currencySymbol = computed(() => page.props.currencySymbol || '$');

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') {
        return {r: 134, g: 239, b: 172};
    }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return rgbToHex(newR, newG, newB);
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));
const expandedNodes = reactive({});

const showToast = (message, type = 'success') => {
    const toastEvent = new CustomEvent('show-toast', {
        detail: {message, type}
    });
    window.dispatchEvent(toastEvent);
};

const toggleNode = (nodeId) => {
    expandedNodes[nodeId] = !expandedNodes[nodeId];
};

const expandAll = () => {
    const expandRecursively = (nodes) => {
        nodes.forEach(node => {
            expandedNodes[node.id] = true;
            if (node.children && node.children.length > 0) {
                expandRecursively(node.children);
            }
        });
    };
    if (props.network && props.network.length > 0) {
        expandRecursively(props.network);
        showToast(t('allNodesExpanded') || 'All nodes expanded', 'success');
    }
};

const collapseAll = () => {
    Object.keys(expandedNodes).forEach(key => {
        expandedNodes[key] = false;
    });
    showToast(t('allNodesCollapsed') || 'All nodes collapsed', 'info');
};

const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '0.00';
    return parseFloat(value).toFixed(2);
};

onMounted(() => {
    try {
        const flash = page.props.flash;
        if (flash?.success) showToast(flash.success, 'success');
        if (flash?.error) showToast(flash.error, 'error');
        if (flash?.warning) showToast(flash.warning, 'warning');
        if (flash?.info) showToast(flash.info, 'info');
    } catch (error) {
        console.error('Component mount error:', error);
    }
});
</script>

<template>
    <UserLayout :page-title="t('network')" :page-section="t('referralNetwork')">
        <div class="rnw-page">

            <!-- Welcome strip -->
            <div class="rnw-welcome anim-up" style="--d:0ms">
                <div class="rnww-left">
                    <div class="rnww-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                        </svg>
                    </div>
                    <div class="rnww-text">
                        <span class="rnww-eye">{{ t('referral') }}</span>
                        <h1 class="rnww-title">{{ t('network') }}</h1>
                    </div>
                </div>
                <div class="rnww-chips">
                    <span class="rnww-chip rnww-chip--purple">{{ stats.total_network }} {{ t('total') }}</span>
                    <span class="rnww-chip rnww-chip--green">{{ stats.active_members }} {{ t('active') }}</span>
                    <span class="rnww-chip rnww-chip--amber">{{ stats.network_depth }} {{ t('levels') }}</span>
                </div>
            </div>

            <!-- 4 Stat cards -->
            <div class="rnw-stats anim-up" style="--d:60ms">
                <div class="rnw-stat" style="--ac:167,93,237">
                    <div class="rnw-glow"></div>
                    <div class="rnw-stat-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                    </div>
                    <div class="rnw-stat-val">{{ stats.total_network }}</div>
                    <div class="rnw-stat-lbl">{{ t('totalNetwork') }}</div>
                    <div class="rnw-stat-sub">{{ t('allLevels') }}</div>
                </div>
                <div class="rnw-stat" style="--ac:34,197,94">
                    <div class="rnw-glow"></div>
                    <div class="rnw-stat-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                    </div>
                    <div class="rnw-stat-val">{{ stats.active_members }}</div>
                    <div class="rnw-stat-lbl">{{ t('activeMembers') }}</div>
                    <div class="rnw-stat-sub">{{ t('currentlyActive') }}</div>
                </div>
                <div class="rnw-stat" style="--ac:251,191,36">
                    <div class="rnw-glow"></div>
                    <div class="rnw-stat-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                    </div>
                    <div class="rnw-stat-val">{{ currencySymbol }}{{ formatCurrency(stats.total_earnings) }}</div>
                    <div class="rnw-stat-lbl">{{ t('totalEarnings') }}</div>
                    <div class="rnw-stat-sub">{{ t('fromNetwork') }}</div>
                </div>
                <div class="rnw-stat" style="--ac:59,130,246">
                    <div class="rnw-glow"></div>
                    <div class="rnw-stat-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                        </svg>
                    </div>
                    <div class="rnw-stat-val">{{ stats.network_depth }}</div>
                    <div class="rnw-stat-lbl">{{ t('networkDepth') }}</div>
                    <div class="rnw-stat-sub">{{ t('maxLevels') }}</div>
                </div>
            </div>

            <!-- Network hierarchy card -->
            <div class="rnw-card anim-up" style="--d:120ms">
                <div class="rnw-card-head">
                    <div>
                        <h3 class="rnw-card-title">{{ t('networkHierarchy') }}</h3>
                        <p class="rnw-card-sub">{{ t('clickToExpand') }}</p>
                    </div>
                    <div class="rnw-ctrl-btns">
                        <button @click="expandAll" class="rnw-btn-expand">{{ t('expandAll') }}</button>
                        <button @click="collapseAll" class="rnw-btn-collapse">{{ t('collapseAll') }}</button>
                    </div>
                </div>

                <div class="rnw-tree-body">
                    <div v-if="network && network.length > 0" class="rnw-tree-list">
                        <NetworkNode
                            v-for="node in network"
                            :key="node.id"
                            :node="node"
                            :level="node.level"
                            :is-expanded="expandedNodes[node.id] || false"
                            :theme-colors="themeColors"
                            :currency-symbol="currencySymbol"
                            :expanded-nodes="expandedNodes"
                            @toggle="toggleNode"
                        />
                    </div>
                    <div v-else class="rnw-empty">
                        <div class="rnw-empty-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                            </svg>
                        </div>
                        <h3 class="rnw-empty-title">{{ t('noReferralsYet') }}</h3>
                        <p class="rnw-empty-desc">{{ t('startBuildingNetwork') }}</p>
                        <button @click="() => $inertia.visit('/user/referral')" class="rnw-btn-refer">
                            {{ t('viewReferralCode') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Referral Bonuses -->
            <div v-if="referralBonuses && referralBonuses.length > 0" class="rnw-bonuses anim-up" style="--d:180ms">
                <div class="rnw-bonuses-head">
                    <h3 class="rnw-bonuses-title">{{ t('referralBonuses') }}</h3>
                    <p class="rnw-bonuses-sub">{{ t('achieveTheseBonus') }}</p>
                </div>

                <!-- Milestone bonuses -->
                <div v-if="referralBonuses.filter(b => b.type === 'milestone').length > 0">
                    <div class="rnw-bonus-section-head">
                        <span class="rnw-bonus-dot rnw-bonus-dot--purple"></span>
                        {{ t('milestoneBonuses') }}
                    </div>
                    <div class="rnw-bonus-grid">
                        <div v-for="bonus in referralBonuses.filter(b => b.type === 'milestone')" :key="bonus.id"
                             class="rnw-bonus-card"
                             :class="bonus.is_claimed ? 'rnw-bonus-card--claimed' : bonus.is_eligible ? 'rnw-bonus-card--eligible' : ''">
                            <div v-if="bonus.is_claimed" class="rnw-bonus-badge rnw-bonus-badge--green">
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                </svg>
                                {{ t('claimed') }}
                            </div>
                            <div v-else-if="bonus.is_eligible" class="rnw-bonus-badge rnw-bonus-badge--purple">
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                                {{ t('eligible') }}
                            </div>
                            <h5 class="rnw-bonus-name">{{ bonus.name }}</h5>
                            <p class="rnw-bonus-desc">{{ bonus.description }}</p>
                            <div class="rnw-bonus-reward">
                                <p class="rnw-bonus-reward-lbl">{{ t('reward') }}</p>
                                <p class="rnw-bonus-reward-val">{{ currencySymbol }}{{ formatCurrency(bonus.reward_amount) }}</p>
                                <p class="rnw-bonus-reward-note">{{ bonus.reward_details }}</p>
                            </div>
                            <div class="rnw-bonus-progress-wrap">
                                <div v-if="bonus.min_active_referrals > 0" class="rnw-bonus-prog">
                                    <div class="rnw-prog-row">
                                        <span>{{ t('activeReferrals') }}</span>
                                        <span>{{ bonus.referral_progress }}%</span>
                                    </div>
                                    <div class="rnw-prog-track">
                                        <div class="rnw-prog-fill rnw-prog-fill--purple" :style="`width:${bonus.referral_progress}%`"></div>
                                    </div>
                                    <p class="rnw-prog-note">{{ stats.active_members }} / {{ bonus.min_active_referrals }} {{ t('referrals') }}</p>
                                </div>
                                <div v-if="bonus.min_team_investment > 0" class="rnw-bonus-prog">
                                    <div class="rnw-prog-row">
                                        <span>{{ t('teamInvestment') }}</span>
                                        <span>{{ bonus.investment_progress }}%</span>
                                    </div>
                                    <div class="rnw-prog-track">
                                        <div class="rnw-prog-fill rnw-prog-fill--purple" :style="`width:${bonus.investment_progress}%`"></div>
                                    </div>
                                    <p class="rnw-prog-note">{{ currencySymbol }}{{ formatCurrency(stats.team_investment || 0) }} / {{ currencySymbol }}{{ formatCurrency(bonus.min_team_investment) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Achievement bonuses -->
                <div v-if="referralBonuses.filter(b => b.type === 'achievement').length > 0" class="rnw-bonus-section">
                    <div class="rnw-bonus-section-head">
                        <span class="rnw-bonus-dot rnw-bonus-dot--purple"></span>
                        {{ t('achievementBonuses') }}
                    </div>
                    <div class="rnw-bonus-grid rnw-bonus-grid--2col">
                        <div v-for="bonus in referralBonuses.filter(b => b.type === 'achievement')" :key="bonus.id"
                             class="rnw-bonus-card"
                             :class="bonus.is_claimed ? 'rnw-bonus-card--claimed' : bonus.is_eligible ? 'rnw-bonus-card--eligible' : ''">
                            <div v-if="bonus.is_claimed" class="rnw-bonus-badge rnw-bonus-badge--green">
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                </svg>
                                {{ t('claimed') }}
                            </div>
                            <div class="rnw-ach-row">
                                <div class="rnw-ach-icon">
                                    <svg fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                    </svg>
                                </div>
                                <div class="flex-1">
                                    <h5 class="rnw-bonus-name">{{ bonus.name }}</h5>
                                    <p class="rnw-bonus-desc">{{ bonus.description }}</p>
                                    <div class="rnw-ach-foot">
                                        <span class="rnw-ach-amount">{{ currencySymbol }}{{ formatCurrency(bonus.reward_amount) }}</span>
                                        <span v-if="bonus.time_frame_days > 0" class="rnw-ach-days">({{ bonus.time_frame_days }} {{ t('days') }})</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Loyalty bonuses -->
                <div v-if="referralBonuses.filter(b => b.type === 'loyalty').length > 0" class="rnw-bonus-section">
                    <div class="rnw-bonus-section-head">
                        <span class="rnw-bonus-dot rnw-bonus-dot--amber"></span>
                        {{ t('loyaltyBonuses') }}
                    </div>
                    <div class="rnw-bonus-grid">
                        <div v-for="bonus in referralBonuses.filter(b => b.type === 'loyalty')" :key="bonus.id"
                             class="rnw-bonus-card">
                            <div class="rnw-loy-head">
                                <div class="rnw-loy-icon">
                                    <svg fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clip-rule="evenodd"/>
                                    </svg>
                                </div>
                                <div>
                                    <h5 class="rnw-bonus-name">{{ bonus.name }}</h5>
                                    <span class="rnw-loy-badge">{{ t('recurring') }}</span>
                                </div>
                            </div>
                            <p class="rnw-bonus-desc">{{ bonus.description }}</p>
                            <div class="rnw-loy-reward">
                                <p class="rnw-bonus-reward-lbl">{{ t('reward') }}</p>
                                <p class="rnw-loy-val">{{ currencySymbol }}{{ formatCurrency(bonus.reward_amount) }}</p>
                                <p class="rnw-loy-period">{{ t('every') }} {{ bonus.time_frame_days }} {{ t('days') }}</p>
                            </div>
                            <p class="rnw-loy-req">{{ t('requires') }}: {{ bonus.min_active_referrals }}+ {{ t('activeReferrals') }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- How it works + network levels -->
            <div class="rnw-info-row anim-up" style="--d:240ms">
                <!-- How it works -->
                <div class="rnw-info-card">
                    <div class="rnw-info-head">
                        <div class="rnw-info-icon rnw-info-icon--purple">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <h3 class="rnw-info-title">{{ t('howItWorks') }}</h3>
                    </div>
                    <ul class="rnw-steps">
                        <li v-for="(step, i) in [t('shareReferralCode'), t('whenTheySignUp'), t('earnCommissions'), t('buildDeeperNetwork')]"
                            :key="i" class="rnw-step">
                            <span class="rnw-step-num">{{ i + 1 }}</span>
                            <span class="rnw-step-text">{{ step }}</span>
                        </li>
                    </ul>
                </div>

                <!-- Network levels -->
                <div class="rnw-info-card">
                    <div class="rnw-info-head">
                        <div class="rnw-info-icon rnw-info-icon--blue">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                            </svg>
                        </div>
                        <h3 class="rnw-info-title">{{ t('networkLevels') }}</h3>
                    </div>
                    <div class="rnw-levels">
                        <div class="rnw-level-row">
                            <span class="rnw-level-lbl">{{ t('level1Direct') }}</span>
                            <span class="rnw-level-val rnw-level-val--purple">{{ network.filter(n => n.level === 1).length }} {{ t('members') }}</span>
                        </div>
                        <div v-if="stats.network_depth >= 2" class="rnw-level-row">
                            <span class="rnw-level-lbl">{{ t('level2') }}</span>
                            <span class="rnw-level-val rnw-level-val--green">{{ t('active') }}</span>
                        </div>
                        <div v-if="stats.network_depth >= 3" class="rnw-level-row">
                            <span class="rnw-level-lbl">{{ t('level3') }}</span>
                            <span class="rnw-level-val rnw-level-val--green">{{ t('active') }}</span>
                        </div>
                        <div v-if="stats.network_depth >= 4" class="rnw-level-row">
                            <span class="rnw-level-lbl">{{ t('level4Plus') }}</span>
                            <span class="rnw-level-val rnw-level-val--green">{{ t('active') }}</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </UserLayout>
</template>

<style scoped>
@keyframes slideUp {
    from { opacity:0; transform:translateY(22px) }
    to   { opacity:1; transform:none }
}
.anim-up {
    animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both;
    animation-delay: var(--d,0ms);
}

.rnw-page {
    padding: 1.5rem 1rem 3rem;
    display: flex; flex-direction: column; gap: 1.25rem;
    max-width: 1400px; margin: 0 auto;
}
@media (min-width:640px)  { .rnw-page { padding: 2rem 1.5rem 3rem; } }
@media (min-width:1024px) { .rnw-page { padding: 2.5rem 2rem 3rem; } }

/* welcome */
.rnw-welcome {
    display:flex; align-items:center; justify-content:space-between;
    gap:1rem; flex-wrap:wrap;
    background:linear-gradient(135deg,rgba(20,10,40,0.92),rgba(30,14,60,0.88));
    border:1px solid rgba(167,93,237,0.22); border-radius:1.25rem;
    padding:1rem 1.25rem; position:relative; overflow:hidden;
}
.rnw-welcome::before {
    content:''; position:absolute; inset:0 0 auto 0; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.5),transparent);
}
.rnww-left { display:flex; align-items:center; gap:.875rem; }
.rnww-icon {
    width:2.75rem; height:2.75rem; border-radius:.875rem;
    background:rgba(167,93,237,0.18); border:1px solid rgba(167,93,237,0.32);
    display:flex; align-items:center; justify-content:center; flex-shrink:0; color:#a75ded;
}
.rnww-icon svg { width:1.25rem; height:1.25rem; }
.rnww-eye   { font-size:.65rem; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:rgba(167,93,237,0.8); display:block; }
.rnww-title { font-size:1.25rem; font-weight:800; color:#fff; margin:0; }
.rnww-chips { display:flex; align-items:center; gap:.625rem; flex-wrap:wrap; }
.rnww-chip {
    display:inline-flex; padding:.35rem .85rem; border-radius:9999px;
    font-size:.72rem; font-weight:700;
}
.rnww-chip--purple { background:rgba(167,93,237,0.15); border:1px solid rgba(167,93,237,0.35); color:#c084fc; }
.rnww-chip--green  { background:rgba(34,197,94,0.12);  border:1px solid rgba(34,197,94,0.3);   color:#4ade80; }
.rnww-chip--amber  { background:rgba(251,191,36,0.12); border:1px solid rgba(251,191,36,0.28); color:#fbbf24; }

/* stats */
.rnw-stats { display:grid; grid-template-columns:repeat(2,1fr); gap:.875rem; }
@media (min-width:900px) { .rnw-stats { grid-template-columns:repeat(4,1fr); } }
.rnw-stat {
    position:relative;
    background:linear-gradient(135deg,rgba(20,10,40,0.92),rgba(14,6,28,0.95));
    border:1px solid rgba(var(--ac),.22); border-radius:1.125rem;
    padding:1.25rem 1rem 1rem; overflow:hidden;
    transition:border-color .25s, transform .25s;
}
.rnw-stat:hover { border-color:rgba(var(--ac),.5); transform:translateY(-2px); }
.rnw-stat::before {
    content:''; position:absolute; top:0; left:0; right:0; height:1px;
    background:linear-gradient(90deg,transparent,rgba(var(--ac),.5),transparent);
}
.rnw-glow {
    position:absolute; top:-20px; right:-20px; width:100px; height:100px;
    border-radius:50%; background:radial-gradient(circle,rgba(var(--ac),.22) 0%,transparent 70%);
    pointer-events:none;
}
.rnw-stat-icon {
    width:2.25rem; height:2.25rem; border-radius:.625rem;
    background:rgba(var(--ac),.15); border:1px solid rgba(var(--ac),.28);
    display:flex; align-items:center; justify-content:center;
    margin-bottom:.75rem; color:rgb(var(--ac));
}
.rnw-stat-icon svg { width:1.1rem; height:1.1rem; }
.rnw-stat-val { font-size:1.5rem; font-weight:800; color:#fff; line-height:1.1; margin-bottom:.3rem; word-break:break-all; }
.rnw-stat-lbl { font-size:.72rem; font-weight:600; color:rgba(255,255,255,0.45); text-transform:uppercase; letter-spacing:.06em; }
.rnw-stat-sub { font-size:.68rem; color:rgba(255,255,255,0.28); margin-top:.2rem; }

/* card */
.rnw-card {
    background:linear-gradient(135deg,rgba(20,10,40,0.92),rgba(14,6,28,0.95));
    border:1px solid rgba(167,93,237,0.14); border-radius:1.25rem;
    overflow:hidden; position:relative;
}
.rnw-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.rnw-card-head {
    display:flex; align-items:center; justify-content:space-between;
    flex-wrap:wrap; gap:.75rem;
    padding:1.25rem; border-bottom:1px solid rgba(255,255,255,0.06);
}
.rnw-card-title { font-size:1rem; font-weight:700; color:#fff; }
.rnw-card-sub   { font-size:.72rem; color:rgba(255,255,255,0.4); margin-top:.15rem; }
.rnw-ctrl-btns  { display:flex; gap:.5rem; }
.rnw-btn-expand {
    padding:.45rem 1rem; border-radius:.625rem; font-size:.78rem; font-weight:700; cursor:pointer;
    background:rgba(167,93,237,0.2); border:1px solid rgba(167,93,237,0.4); color:#c084fc;
    transition:all .2s;
}
.rnw-btn-expand:hover { background:rgba(167,93,237,0.35); }
.rnw-btn-collapse {
    padding:.45rem 1rem; border-radius:.625rem; font-size:.78rem; font-weight:700; cursor:pointer;
    background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.12); color:rgba(255,255,255,0.55);
    transition:all .2s;
}
.rnw-btn-collapse:hover { background:rgba(255,255,255,0.1); color:#fff; }

.rnw-tree-body { padding:1.25rem; }
.rnw-tree-list  { display:flex; flex-direction:column; gap:.625rem; }

.rnw-empty { text-align:center; padding:4rem 1rem; display:flex; flex-direction:column; align-items:center; gap:1rem; }
.rnw-empty-icon {
    width:5rem; height:5rem; border-radius:50%;
    background:rgba(167,93,237,0.1); border:1px solid rgba(167,93,237,0.2);
    display:flex; align-items:center; justify-content:center; color:#a75ded;
}
.rnw-empty-icon svg { width:2.5rem; height:2.5rem; }
.rnw-empty-title { font-size:1.1rem; font-weight:700; color:#fff; }
.rnw-empty-desc  { font-size:.875rem; color:rgba(255,255,255,0.4); max-width:24rem; }
.rnw-btn-refer {
    padding:.625rem 1.5rem; border-radius:.75rem; font-size:.85rem; font-weight:700; cursor:pointer;
    background:rgba(167,93,237,0.25); border:1px solid rgba(167,93,237,0.45); color:#c084fc;
    transition:all .2s;
}
.rnw-btn-refer:hover { background:rgba(167,93,237,0.4); }

/* bonuses */
.rnw-bonuses { display:flex; flex-direction:column; gap:1.5rem; }
.rnw-bonuses-head .rnw-bonuses-title { font-size:1.25rem; font-weight:800; color:#fff; }
.rnw-bonuses-head .rnw-bonuses-sub   { font-size:.78rem; color:rgba(255,255,255,0.4); margin-top:.25rem; }
.rnw-bonus-section { display:flex; flex-direction:column; gap:.875rem; }
.rnw-bonus-section-head {
    display:flex; align-items:center; gap:.5rem;
    font-size:.9rem; font-weight:700; color:#fff;
}
.rnw-bonus-dot { width:.5rem; height:.5rem; border-radius:50%; }
.rnw-bonus-dot--purple { background:#a855f7; }
.rnw-bonus-dot--amber  { background:#fbbf24; }

.rnw-bonus-grid {
    display:grid; grid-template-columns:1fr; gap:.875rem;
}
@media (min-width:640px)  { .rnw-bonus-grid { grid-template-columns:1fr 1fr; } }
@media (min-width:1024px) { .rnw-bonus-grid { grid-template-columns:repeat(3,1fr); } }
.rnw-bonus-grid--2col { grid-template-columns:1fr; }
@media (min-width:640px) { .rnw-bonus-grid--2col { grid-template-columns:1fr 1fr; } }

.rnw-bonus-card {
    position:relative;
    background:linear-gradient(135deg,rgba(20,10,40,0.92),rgba(14,6,28,0.95));
    border:1px solid rgba(167,93,237,0.14); border-radius:1rem;
    padding:1.25rem; overflow:hidden;
    transition:border-color .2s;
}
.rnw-bonus-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.3),transparent);
}
.rnw-bonus-card--claimed  { border-color:rgba(34,197,94,0.3); }
.rnw-bonus-card--eligible { border-color:rgba(167,93,237,0.4); }

.rnw-bonus-badge {
    display:inline-flex; align-items:center; gap:.3rem;
    position:absolute; top:.875rem; right:.875rem;
    padding:.2rem .65rem; border-radius:9999px;
    font-size:.65rem; font-weight:700;
}
.rnw-bonus-badge svg { width:.7rem; height:.7rem; }
.rnw-bonus-badge--green  { background:rgba(34,197,94,0.15);  border:1px solid rgba(34,197,94,0.3);  color:#4ade80; }
.rnw-bonus-badge--purple { background:rgba(167,93,237,0.15); border:1px solid rgba(167,93,237,0.3); color:#c084fc; }

.rnw-bonus-name { font-size:.9rem; font-weight:700; color:#fff; margin-bottom:.35rem; padding-right:4rem; }
.rnw-bonus-desc { font-size:.75rem; color:rgba(255,255,255,0.45); margin-bottom:.875rem; line-height:1.5; }
.rnw-bonus-reward {
    padding:.75rem; border-radius:.625rem; margin-bottom:.875rem;
    background:rgba(167,93,237,0.08); border:1px solid rgba(167,93,237,0.15);
}
.rnw-bonus-reward-lbl  { font-size:.65rem; font-weight:600; text-transform:uppercase; letter-spacing:.07em; color:rgba(255,255,255,0.4); margin-bottom:.2rem; }
.rnw-bonus-reward-val  { font-size:1.4rem; font-weight:800; color:#c084fc; }
.rnw-bonus-reward-note { font-size:.7rem; color:rgba(255,255,255,0.35); margin-top:.2rem; }

.rnw-bonus-prog { margin-bottom:.5rem; }
.rnw-prog-row { display:flex; justify-content:space-between; font-size:.72rem; color:rgba(255,255,255,0.5); margin-bottom:.35rem; }
.rnw-prog-row span:last-child { font-weight:700; color:#fff; }
.rnw-prog-track { height:.5rem; border-radius:9999px; background:rgba(255,255,255,0.06); overflow:hidden; }
.rnw-prog-fill  { height:100%; border-radius:9999px; transition:width .5s ease-out; }
.rnw-prog-fill--purple { background:linear-gradient(90deg,#7c3aed,#c084fc); }
.rnw-prog-note { font-size:.68rem; color:rgba(255,255,255,0.3); margin-top:.25rem; }

/* achievement */
.rnw-ach-row  { display:flex; gap:.875rem; align-items:flex-start; }
.rnw-ach-icon {
    width:3rem; height:3rem; flex-shrink:0; border-radius:.75rem;
    background:rgba(168,85,247,0.15); border:1px solid rgba(168,85,247,0.3);
    display:flex; align-items:center; justify-content:center; color:#c084fc;
}
.rnw-ach-icon svg { width:1.25rem; height:1.25rem; }
.rnw-ach-foot { display:flex; align-items:center; gap:.5rem; margin-top:.5rem; }
.rnw-ach-amount { font-size:1.1rem; font-weight:800; color:#c084fc; }
.rnw-ach-days   { font-size:.72rem; color:rgba(255,255,255,0.35); }

/* loyalty */
.rnw-loy-head { display:flex; gap:.75rem; align-items:center; margin-bottom:.875rem; }
.rnw-loy-icon {
    width:2.5rem; height:2.5rem; flex-shrink:0; border-radius:.625rem;
    background:rgba(251,191,36,0.12); border:1px solid rgba(251,191,36,0.25);
    display:flex; align-items:center; justify-content:center; color:#fbbf24;
}
.rnw-loy-icon svg { width:1.1rem; height:1.1rem; }
.rnw-loy-badge {
    display:inline-block; margin-top:.2rem;
    padding:.15rem .5rem; border-radius:9999px;
    font-size:.65rem; font-weight:700;
    background:rgba(251,191,36,0.12); border:1px solid rgba(251,191,36,0.28); color:#fbbf24;
}
.rnw-loy-reward { padding:.75rem; border-radius:.625rem; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); margin:.75rem 0; }
.rnw-loy-val    { font-size:1.4rem; font-weight:800; color:#fbbf24; }
.rnw-loy-period { font-size:.7rem; color:rgba(255,255,255,0.35); margin-top:.2rem; }
.rnw-loy-req    { font-size:.72rem; color:rgba(255,255,255,0.35); }

/* info row */
.rnw-info-row {
    display:grid; grid-template-columns:1fr; gap:1rem;
}
@media (min-width:900px) { .rnw-info-row { grid-template-columns:1fr 1fr; } }
.rnw-info-card {
    background:linear-gradient(135deg,rgba(20,10,40,0.92),rgba(14,6,28,0.95));
    border:1px solid rgba(167,93,237,0.14); border-radius:1.25rem;
    padding:1.5rem; position:relative; overflow:hidden;
}
.rnw-info-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.rnw-info-head { display:flex; align-items:center; gap:.875rem; margin-bottom:1.25rem; }
.rnw-info-icon {
    width:2.5rem; height:2.5rem; border-radius:.75rem;
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
}
.rnw-info-icon svg { width:1.25rem; height:1.25rem; }
.rnw-info-icon--purple { background:rgba(167,93,237,0.15); border:1px solid rgba(167,93,237,0.3); color:#c084fc; }
.rnw-info-icon--blue   { background:rgba(59,130,246,0.15);  border:1px solid rgba(59,130,246,0.3);  color:#60a5fa; }
.rnw-info-title { font-size:1rem; font-weight:700; color:#fff; }
.rnw-steps { display:flex; flex-direction:column; gap:.875rem; list-style:none; margin:0; padding:0; }
.rnw-step  { display:flex; align-items:flex-start; gap:.875rem; }
.rnw-step-num {
    width:2rem; height:2rem; flex-shrink:0; border-radius:50%;
    background:rgba(167,93,237,0.15); border:1px solid rgba(167,93,237,0.3);
    display:flex; align-items:center; justify-content:center;
    font-size:.78rem; font-weight:800; color:#c084fc;
}
.rnw-step-text { font-size:.83rem; color:rgba(255,255,255,0.65); padding-top:.35rem; line-height:1.5; }

.rnw-levels { display:flex; flex-direction:column; gap:.5rem; }
.rnw-level-row {
    display:flex; align-items:center; justify-content:space-between;
    padding:.75rem 1rem; border-radius:.625rem;
    background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06);
}
.rnw-level-lbl { font-size:.83rem; color:rgba(255,255,255,0.6); }
.rnw-level-val { font-size:.83rem; font-weight:700; }
.rnw-level-val--purple { color:#c084fc; }
.rnw-level-val--green  { color:#4ade80; }
</style>
